import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

// ── Icônes ───────────────────────────────────────────────────
const IconMenu = ({ size = 22, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M3 6h18M3 12h18M3 18h18"/>
  </svg>
);
const IconClose = ({ size = 22, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);
const IconUser = ({ size = 18, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconChevron = ({ size = 14, color = "#7A5A3A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IconLogout = ({ size = 15, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconProfil = ({ size = 15, color = "#1C1008" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

export default function Navbar({ transparent = false }) {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [dropOpen, setDropOpen]   = useState(false);
  const [user, setUser]           = useState(null);
  const dropRef                   = useRef(null);
  const navigate                  = useNavigate();

  // ── Lecture du user dans localStorage ──
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  // ── Scroll ──
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { if (scrolled) setMenuOpen(false); }, [scrolled]);

  // ── Ferme le dropdown si clic dehors ──
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setDropOpen(false);
    navigate("/");
  };

  const isLight = !transparent || scrolled;

  // ── Liens de navigation ──
  // NOTE : les chemins /transport, /loisirs et /artisans sont une hypothèse
  // basée sur le nom des composants/tables existants. À vérifier contre
  // le fichier de routes (App.jsx ou équivalent) et corriger si besoin.
  const navLinks = [
    { label: "Accueil",      path: "/" },
    { label: "Hébergements", path: "/hotels" },
    { label: "Restaurants",  path: "/restaurants" },
    { label: "Guides",       path: "/guides" },
    { label: "Transport",    path: "/transport" },
    { label: "Loisirs",      path: "/loisirs" },
    { label: "Artisans",     path: "/artisans" },
    { label: "Contact",      path: "/contact" },
  ];

  // ── Initiales pour l'avatar ──
  const getInitiales = (u) => {
    if (!u) return "?";
    const prenom = u.prenom || u.nom || u.email || "";
    const nom    = u.nom    || "";
    return (prenom[0] || "") + (nom[0] || "");
  };

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .menu-slide   { animation: slideDown .25s ease forwards; }
        .drop-slide   { animation: slideDown .2s ease forwards; }
        .nav-link-item {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 14px; font-weight: 500;
          opacity: 0.75; cursor: pointer;
          transition: opacity .2s; text-decoration: none;
          white-space: nowrap;
        }
        .nav-link-item:hover { opacity: 1; }
        .drop-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 16px; font-family: 'Space Grotesk', sans-serif;
          font-size: 14px; font-weight: 500; color: #1C1008;
          cursor: pointer; transition: background .15s; text-decoration: none;
          border-radius: 8px;
        }
        .drop-item:hover { background: rgba(196,68,26,.06); }
        .drop-item-danger { color: #C4441A !important; }
        .drop-item-danger:hover { background: rgba(196,68,26,.08) !important; }
        @media(max-width:767px){
          .nav-desktop  { display: none !important; }
          .nav-auth     { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
        @media(min-width:768px){
          .nav-hamburger { display: none !important; }
        }
      `}</style>

      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        background: isLight ? "rgba(250,245,236,0.96)" : "rgba(28,16,8,0.3)",
        backdropFilter: isLight ? "blur(14px)" : "none",
        borderBottom: isLight ? "1px solid rgba(28,16,8,.07)" : "none",
        transition: "all .35s", padding: "0 5%",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>

          {/* ── Logo ── */}
         <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
  <div style={{ width: 34, height: 34, borderRadius: 8, background: "#C4441A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <svg width="20" height="20" viewBox="0 0 100 100">
      <circle cx="50" cy="38" r="22" fill="white"/>
      <rect x="45" y="55" width="10" height="20" rx="2" fill="white"/>
      <ellipse cx="50" cy="78" rx="16" ry="3" fill="white" opacity="0.5"/>
    </svg>
  </div>
  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 21, fontWeight: 800, letterSpacing: "-0.5px", color: isLight ? "#1C1008" : "white" }}>
    Senu<span style={{ color: "#C4441A" }}>Tour</span>
  </span>
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 700, background: "rgba(196,68,26,.15)", color: "#C4441A", padding: "2px 7px", borderRadius: 4, letterSpacing: 1, textTransform: "uppercase" }}>
    Beta
  </span>
</Link>

          {/* ── Liens desktop ── */}
          <div className="nav-desktop" style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
            {navLinks.map(l => (
              <Link key={l.label} to={l.path} className="nav-link-item" style={{ color: isLight ? "#1C1008" : "white" }}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* ── Actions ── */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

            {user ? (
              /* ── CONNECTÉ : avatar + dropdown ── */
              <div ref={dropRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setDropOpen(!dropOpen)}
                  style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: `1.5px solid ${isLight ? "rgba(28,16,8,.15)" : "rgba(255,255,255,.25)"}`, borderRadius: 50, padding: "6px 12px 6px 6px", cursor: "pointer", transition: "all .2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#C4441A"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = isLight ? "rgba(28,16,8,.15)" : "rgba(255,255,255,.25)"}
                >
                  {/* Avatar avec initiales */}
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#C4441A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 700, color: "white", textTransform: "uppercase" }}>
                      {getInitiales(user)}
                    </span>
                  </div>
                  {/* Nom */}
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, color: isLight ? "#1C1008" : "white", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user.prenom || user.nom || "Mon compte"}
                  </span>
                  <IconChevron size={13} color={isLight ? "#7A5A3A" : "rgba(255,255,255,.7)"} />
                </button>

                {/* Dropdown */}
                {dropOpen && (
                  <div className="drop-slide" style={{ position: "absolute", top: "calc(100% + 10px)", right: 0, background: "white", borderRadius: 12, border: "1.5px solid rgba(28,16,8,.08)", boxShadow: "0 8px 32px rgba(28,16,8,.12)", minWidth: 200, padding: "8px", zIndex: 300 }}>

                    {/* Infos user */}
                    <div style={{ padding: "10px 14px 12px", borderBottom: "1px solid rgba(28,16,8,.06)", marginBottom: 6 }}>
                      <p style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 700, color: "#1C1008", margin: "0 0 2px" }}>
                        {user.prenom} {user.nom}
                      </p>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "#7A5A3A", margin: 0 }}>
                        {user.email}
                      </p>
                    </div>

                    {/* Liens */}
                    <Link to="/profil" className="drop-item" onClick={() => setDropOpen(false)}>
                      <IconProfil size={15} color="#1C1008" />
                      Mon profil
                    </Link>
                    <Link to="/profil" className="drop-item" onClick={() => setDropOpen(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1C1008" strokeWidth="2" strokeLinecap="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Mes réservations
                    </Link>

                    <div style={{ borderTop: "1px solid rgba(28,16,8,.06)", marginTop: 6, paddingTop: 6 }}>
                      <button className="drop-item drop-item-danger" onClick={handleLogout} style={{ width: "100%", background: "none", border: "none", textAlign: "left" }}>
                        <IconLogout size={15} color="#C4441A" />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── NON CONNECTÉ : Connexion + S'inscrire ── */
              <div className="nav-auth" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Link to="/connexion" className="nav-link-item" style={{ color: isLight ? "#1C1008" : "white" }}>
                  Connexion
                </Link>
                <button onClick={() => navigate("/inscription")} style={{
                  background: "#C4441A", color: "white", border: "none",
                  padding: "9px 18px", borderRadius: 8,
                  fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all .3s",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "#A03614"}
                  onMouseLeave={e => e.currentTarget.style.background = "#C4441A"}
                >
                  S'inscrire
                </button>
              </div>
            )}

            {/* Hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="nav-hamburger"
              style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 4 }}>
              {menuOpen
                ? <IconClose size={22} color={isLight ? "#1C1008" : "white"} />
                : <IconMenu  size={22} color={isLight ? "#1C1008" : "white"} />
              }
            </button>
          </div>
        </div>

        {/* ── Menu mobile ── */}
        {menuOpen && (
          <div className="menu-slide" style={{
            background: "rgba(250,245,236,0.98)", backdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(28,16,8,.08)", padding: "20px 5% 24px",
          }}>
            {navLinks.map(l => (
              <Link key={l.label} to={l.path} onClick={() => setMenuOpen(false)} style={{
                display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: 16,
                fontWeight: 500, color: "#1C1008", padding: "12px 0",
                borderBottom: "1px solid rgba(28,16,8,.06)", textDecoration: "none",
              }}>
                {l.label}
              </Link>
            ))}

            {user ? (
              <>
                <Link to="/profil" onClick={() => setMenuOpen(false)} style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 500, color: "#1C1008", padding: "12px 0", borderBottom: "1px solid rgba(28,16,8,.06)", textDecoration: "none" }}>
                  Mon profil
                </Link>
                <button onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 500, color: "#C4441A", padding: "12px 0", borderBottom: "1px solid rgba(28,16,8,.06)", background: "none", border: "none", cursor: "pointer" }}>
                  Se déconnecter
                </button>
              </>
            ) : (
              <Link to="/connexion" onClick={() => setMenuOpen(false)} style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 500, color: "#1C1008", padding: "12px 0", textDecoration: "none" }}>
                Connexion
              </Link>
            )}
          </div>
        )}
      </nav>
    </>
  );
}